<script setup>
import { watch } from "vue";
import { useI18n } from "vue-i18n";
import { useCustomerSearch } from "@/composables/shipment/useCustomerSearch";

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "create-new"]);

const { t } = useI18n();

const {
  customers,
  searchQuery,
  isLoading,
  selectedCustomer,
  searchCustomers,
  selectCustomer,
  getCustomerKey,
} = useCustomerSearch();

// Initialize the component
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      selectCustomer(newVal);
    }
  },
  { immediate: true, deep: true }
);

// Update parent when selection changes
watch(selectedCustomer, (newVal, oldVal) => {
  // Prevent unnecessary duplicate emissions by comparing values
  if (JSON.stringify(newVal) !== JSON.stringify(props.modelValue)) {
    emit("update:modelValue", newVal);
  }
});

// Handle creating a new customer
const handleCreateNew = (customer) => {
  emit("create-new", customer);
};
</script>

<template>
  <VAutocomplete
    v-model="selectedCustomer"
    v-model:search-input="searchQuery"
    :loading="isLoading"
    :label="t('shipment.customer')"
    :items="customers"
    item-title="text"
    item-value="id"
    return-object
    clearable
    :no-filter="true"
    variant="outlined"
    density="comfortable"
    :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
    @update:search="searchCustomers"
    @update:model-value="selectCustomer($event)"
  >
    <!-- Display full customer info in selection -->
    <template #selection="{ item }">
      <span>{{ item.raw.text }}</span>
    </template>

    <!-- Custom item in dropdown -->
    <template #item="{ item }">
      <VListItem
        v-if="item?.raw?.isNew"
        @click.stop="handleCreateNew(item.raw)"
      >
        <VListItemTitle>
          <span class="d-flex align-center text-primary">
            <VIcon icon="tabler-plus" size="small" class="me-2" />
            {{ t("shipment.create_new_customer", { name: item?.raw?.name }) }}
          </span>
        </VListItemTitle>
      </VListItem>

      <VListItem v-else :value="item.raw" @click="selectCustomer(item.raw)">
        <VListItemTitle>
          <div class="d-flex justify-space-between align-center">
            <span
              ><strong>{{ item?.raw?.name }}</strong></span
            >
            <span class="text-caption text-medium-emphasis">{{
              item?.raw?.phone
            }}</span>
          </div>
        </VListItemTitle>
      </VListItem>
    </template>
  </VAutocomplete>
</template>
